/**
 * MDM Value Table Component
 *
 * Purpose: Comprehensive table for displaying and managing master data values
 * Architecture: Configures and renders MDTable with dynamic columns, actions, and filters
 * Dependencies: Material-UI, MDTable, MDM helpers, formatting utilities
 * Author: Development Team
 * Created: 2025-01-21
 */

import React, { useMemo, useCallback, useState } from 'react';
import { Alert, Box, Button, Typography, Chip, IconButton, Tooltip, Paper } from '@mui/material';
import {
  Settings as SettingsIcon,
  CloudUpload as ImportIcon,
  CloudDownload as ExportIcon,
  Restore as RestoreIcon,
  LocalOffer as LabelIcon,
  SwapVert as ReorderIcon,
  SortByAlpha as ResetOrderIcon,
} from '@mui/icons-material';
import Badge from '@mui/material/Badge';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import MDTable from '../md-table/MDTable';
import { MasterDataValue } from '../../types/mdm';
import { FieldDefinition } from '../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import { getScopeChipColor } from '../../constants/mdm';
import { ValueChipColorKey } from '../../theme/orchestrateTheme';
import ValueChip from '../common/ValueChip';
import RowActions, { RowActionButton } from '../common/RowActions';
import {
  getAncestorFieldDefinitions,
  getAncestorOrder,
  sortAncestorFieldDefinitions,
  getAncestorValueName,
  createChipColorLookupMap,
  lookupChipColor,
  getScopeIcon,
  isHierarchicalField,
  isSourceField,
  getEffectiveFieldDefinition,
} from '../../utils/mdmHelpers';
import { mdmCacheKeys } from '../../services/mdmCacheService';
import { useMDMValueOrder } from '../../hooks/useMDMValueOrder';
import { ConfirmDialog } from '../common';

const ORDER_SOURCE_LABELS = {
  tenant: 'Custom order',
  global: 'GLOBAL order',
  alphabetical: 'Alphabetical',
} as const;

/**
 * MDMValueTable Props Interface
 */
export interface MDMValueTableProps {
  /**
   * Array of master data values to display
   */
  values: MasterDataValue[];

  /**
   * Currently selected field definition
   */
  selectedFieldDefinition: FieldDefinition | undefined | null;

  /**
   * All available field definitions (for ancestor lookup in hierarchical fields)
   */
  allFieldDefinitions: FieldDefinition[];

  /**
   * Currently selected tenant ID
   */
  selectedTenant: string;

  /**
   * Whether data is loading
   */
  loading: boolean;

  /**
   * Error message if any
   */
  error: string | null;

  /**
   * Handler for row click (edit value)
   */
  onRowClick: (row: MasterDataValue) => void;

  /**
   * Handler for refresh button
   */
  onRefresh: () => void;

  /**
   * Handler for create new value
   */
  onCreateValue?: () => void;

  /**
   * Handler for edit value
   */
  onEditValue: (value: MasterDataValue) => void;

  /**
   * Handler for delete value
   */
  onDeleteValue: (value: MasterDataValue) => void;

  /**
   * Handler for toggle active/inactive
   */
  onToggleActive: (value: MasterDataValue) => void;

  /**
   * Handler for revert override
   */
  onRevertValue: (value: MasterDataValue) => void;

  /**
   * Handler for opening the AliasManagerDrawer for a value (ORCH-115).
   * When provided, the new "Aliases" column renders the alias chip group as
   * a click target. When omitted, the column still renders but is read-only.
   */
  onManageAliases?: (value: MasterDataValue) => void;

  /**
   * Handler for open import modal. Omitted when the user can't import (no button).
   */
  onOpenImport?: () => void;

  /**
   * Handler for open export dialog. Omitted when the user can't export (no button).
   */
  onOpenExport?: () => void;

  /**
   * Custom toolbar filters component (search, scope, include switches)
   */
  customToolbarFilters?: React.ReactNode;

  /**
   * The user's active tenant. List order is written in the active tenant (ORCH-383), so
   * the Reorder and Reset order controls appear only when it is the selected tenant.
   */
  activeTenantId?: string;

  /**
   * Optional test ID for automation
   */
  dataTestId?: string;
}

/**
 * MDM Value Table Component
 *
 * Purpose: Displays master data values in a feature-rich table with dynamic columns
 *
 * Features:
 * - Dynamic column generation based on field type (hierarchical vs regular)
 * - Ancestor columns for hierarchical fields
 * - Scope, status, and audit columns
 * - Row actions (edit, delete, activate/deactivate, revert)
 * - Import/export header actions
 * - Empty state when no selection
 * - Source field handling (read-only mode)
 *
 * @param props - Component props
 * @returns React component
 *
 * @example
 * <MDMValueTable
 *   values={filteredValues}
 *   selectedFieldDefinition={fieldDef}
 *   allFieldDefinitions={allFieldDefs}
 *   selectedTenant="tenant-123"
 *   loading={false}
 *   error={null}
 *   onRowClick={handleEditValue}
 *   onRefresh={handleRefresh}
 *   onCreateValue={handleCreate}
 *   {...otherHandlers}
 * />
 */
export const MDMValueTable: React.FC<MDMValueTableProps> = ({
  values,
  selectedFieldDefinition,
  allFieldDefinitions,
  selectedTenant,
  loading,
  error,
  onRowClick,
  onRefresh,
  onCreateValue,
  onEditValue,
  onDeleteValue,
  onToggleActive,
  onRevertValue,
  onManageAliases,
  onOpenImport,
  onOpenExport,
  customToolbarFilters,
  activeTenantId,
  dataTestId,
}) => {
  // Check if field is hierarchical or source field
  const isHierarchical = useMemo(
    () => isHierarchicalField(selectedFieldDefinition),
    [selectedFieldDefinition]
  );

  const isSource = useMemo(() => isSourceField(selectedFieldDefinition), [selectedFieldDefinition]);

  // List order (ORCH-383): the table shows server order; Reorder mode and Reset order
  // write the active tenant's custom order, so they only appear in that tenant
  const inActiveTenant = !!activeTenantId && activeTenantId === selectedTenant;
  const order = useMDMValueOrder(
    selectedFieldDefinition?.fielddef_id,
    selectedTenant,
    !isSource && inActiveTenant,
    onRefresh
  );
  const [resetOpen, setResetOpen] = useState(false);
  const reordering = order.reorderRows !== null;
  // GLOBAL's own order shows as "Custom order" in GLOBAL; in a tenant "GLOBAL order"
  // means it is inherited
  const orderSource =
    order.source === 'global' && selectedTenant === 'GLOBAL' ? 'tenant' : order.source;
  const hasOwnOrder = orderSource === 'tenant';
  const resetMessage =
    selectedTenant === 'GLOBAL'
      ? 'This list will go back to alphabetical order everywhere that has not set its own order.'
      : 'This list will go back to the GLOBAL order for this tenant.';

  // Get effective field definition for columns (source fields use source field definition)
  const effectiveFieldDefForColumns = useMemo(
    () => getEffectiveFieldDefinition(selectedFieldDefinition, allFieldDefinitions),
    [selectedFieldDefinition, allFieldDefinitions]
  );

  // Get ancestor field definitions for hierarchical fields
  const ancestorFieldDefs = useMemo(() => {
    if (!isHierarchical || !values.length || !selectedFieldDefinition) {
      return [];
    }

    return getAncestorFieldDefinitions(
      values,
      selectedFieldDefinition.fielddef_id,
      allFieldDefinitions
    );
  }, [isHierarchical, values, selectedFieldDefinition, allFieldDefinitions]);

  // Get query client to access MDM cache for related fields
  const queryClient = useQueryClient();

  // Create chip color lookup (must be before columnsBeforeRelatedFields)
  // Include values from current field AND related fields from MDM cache
  const chipColorLookupMap = useMemo(() => {
    const map = createChipColorLookupMap(values);

    // Also include chip colors from related fields (list-type fields in the view)
    // These are stored in the MDM cache and can be accessed via query client
    // We'll enhance the lookup function to check the cache dynamically
    return map;
  }, [values, queryClient, selectedTenant]);

  const chipColorLookup = useCallback(
    (fielddefId: string, valueName: string): string | null => {
      // First check the lookup map (current field values)
      const colorFromMap = lookupChipColor(chipColorLookupMap, fielddefId, valueName);
      if (colorFromMap) {
        return colorFromMap;
      }

      // If not found, check MDM cache for related field values
      try {
        const cacheKey = mdmCacheKeys.values(fielddefId, selectedTenant);
        const cachedData = queryClient.getQueryData<MasterDataValue[]>(cacheKey);

        if (cachedData && Array.isArray(cachedData)) {
          // Find the value by name in the cached data
          const value = cachedData.find((v) => v.name === valueName && v.chip_color);
          if (value?.chip_color) {
            return value.chip_color;
          }
        }
      } catch (error) {
        // Silently fail if cache lookup fails
      }

      return null;
    },
    [chipColorLookupMap, queryClient, selectedTenant]
  );

  // Build columns before related fields (Name + Ancestor Columns)
  const columnsBeforeRelatedFields: GridColDef[] = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: 'name',
        headerName: 'Name',
        width: 250,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as MasterDataValue;
          const chipColor = row.chip_color as ValueChipColorKey | null | undefined;

          // If chip_color is set, render as colored chip
          if (chipColor) {
            return <ValueChip label={row.name} chipColor={chipColor} size="small" />;
          }

          // Default: display as text
          return (
            <Typography variant="body2" noWrap>
              {row.name}
            </Typography>
          );
        },
      },
    ];

    // Add ancestor columns for hierarchical fields
    if (isHierarchical && ancestorFieldDefs.length > 0 && selectedFieldDefinition) {
      const ancestorOrder = getAncestorOrder(values, selectedFieldDefinition.fielddef_id);
      const sortedAncestors = sortAncestorFieldDefinitions(ancestorFieldDefs, ancestorOrder);

      sortedAncestors.forEach((ancestorFieldDef) => {
        columns.push({
          field: `ancestor_${ancestorFieldDef.fielddef_id}`,
          headerName: ancestorFieldDef.label || ancestorFieldDef.fielddef_id,
          width: 200,
          sortable: true,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as MasterDataValue;
            const ancestorName = getAncestorValueName(
              row.hierarchy_path,
              ancestorFieldDef.fielddef_id
            );

            if (!ancestorName) {
              return (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              );
            }

            // Look up chip color for this ancestor value
            const chipColorKey = chipColorLookup(ancestorFieldDef.fielddef_id, ancestorName);

            // If chip_color is found, render as colored chip
            if (chipColorKey) {
              return (
                <ValueChip
                  label={ancestorName}
                  chipColor={chipColorKey as ValueChipColorKey}
                  size="small"
                />
              );
            }

            // Default: display as text
            return (
              <Typography variant="body2" noWrap>
                {ancestorName}
              </Typography>
            );
          },
          valueGetter: (_value: any, row: MasterDataValue) => {
            return getAncestorValueName(row.hierarchy_path, ancestorFieldDef.fielddef_id);
          },
        });
      });
    }

    return columns;
  }, [isHierarchical, ancestorFieldDefs, values, selectedFieldDefinition, chipColorLookup]);

  // Build columns after related fields (Scope, Status, Modified, Actions)
  const columnsAfterRelatedFields: GridColDef[] = useMemo(
    () => [
      {
        field: 'chip_color',
        headerName: 'Color',
        width: 120,
        sortable: true,
        filterable: true,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as MasterDataValue;
          const chipColor = row.chip_color as ValueChipColorKey | null | undefined;

          if (!chipColor) {
            return (
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            );
          }

          return <ValueChip label={chipColor} chipColor={chipColor} size="small" />;
        },
      },
      {
        field: 'scope_info.scope',
        headerName: 'Scope',
        width: 120,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as MasterDataValue;
          const scope = row.scope_info.scope;

          return (
            <Chip
              icon={getScopeIcon(scope) as any}
              label={scope}
              size="small"
              color={getScopeChipColor(scope)}
              sx={{ fontWeight: 600 }}
            />
          );
        },
      },
      {
        field: 'updated_at',
        headerName: 'Modified Date',
        width: 140,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => formatAuditTimestamp(params.value as string),
      },
      {
        field: 'updated_by',
        headerName: 'Modified By',
        width: 120,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => formatAuditUser(params.value as string),
      },
      {
        field: 'is_active',
        headerName: 'Status',
        width: 100,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as MasterDataValue;
          const isActive = row.is_active && !row.is_deleted;
          return (
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'default'}
              size="small"
            />
          );
        },
      },
      {
        field: 'created_at',
        headerName: 'Created',
        width: 140,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => formatAuditTimestamp(params.value as string),
      },
      {
        field: 'created_by',
        headerName: 'Created By',
        width: 120,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => formatAuditUser(params.value as string),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 220,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row as MasterDataValue;

          // Don't show any actions for source fields (read-only)
          if (isSource) {
            return (
              <Typography variant="caption" color="text.disabled">
                Read-only
              </Typography>
            );
          }

          const aliasCount = (row.aliases || []).length;
          const aliasesTruncated = (row as any).aliases_truncated;
          const canDelete =
            row.scope_info.scope !== 'OVERRIDDEN' &&
            (row.scope_info.scope === 'LOCAL' ||
              (row.scope_info.scope === 'GLOBAL' && selectedTenant === 'GLOBAL'));
          return (
            <RowActions
              entityLabel="Value"
              rowLabel={row.name}
              testIdPrefix={`mdm-value-${row.id}`}
              onEdit={row.scope_info.can_edit ? () => onEditValue(row) : undefined}
              secondaryActions={
                <>
                  {/* Manage Aliases (ORCH-115) — badge shows current alias count for the
                    active tenant. List endpoints cap the response, so when the row
                    was returned with aliases_truncated we render a "+" suffix. */}
                  {onManageAliases && (
                    <RowActionButton
                      label={
                        aliasCount === 0
                          ? 'Manage alternate names'
                          : `Manage alternate names (${aliasCount}${aliasesTruncated ? '+' : ''})`
                      }
                      ariaLabel={`Manage alternate names for ${row.name}`}
                      testId={`mdm-manage-aliases-${row.id}`}
                      onClick={() => onManageAliases(row)}
                      icon={
                        <Badge
                          badgeContent={aliasesTruncated ? `${aliasCount}+` : aliasCount}
                          color="primary"
                          invisible={aliasCount === 0}
                          overlap="circular"
                        >
                          <LabelIcon fontSize="small" />
                        </Badge>
                      }
                    />
                  )}
                  {row.scope_info.can_edit && row.scope_info.scope === 'OVERRIDDEN' && (
                    <RowActionButton
                      label="Revert to Global Value"
                      testId={`mdm-value-${row.id}-revert`}
                      onClick={() => onRevertValue(row)}
                      icon={<RestoreIcon fontSize="small" />}
                    />
                  )}
                </>
              }
              deactivation={
                row.scope_info.can_edit
                  ? {
                      variant: 'disable',
                      isActive: row.is_active,
                      onToggle: () => onToggleActive(row),
                    }
                  : undefined
              }
              onDelete={row.scope_info.can_edit && canDelete ? () => onDeleteValue(row) : undefined}
            />
          );
        },
      },
    ],
    [
      isSource,
      selectedTenant,
      onEditValue,
      onToggleActive,
      onDeleteValue,
      onRevertValue,
      onManageAliases,
    ]
  );

  // Custom header actions (list order, Import/Export buttons)
  const customHeaderActions = useMemo(
    () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {orderSource && inActiveTenant && !isSource && (
          <Chip
            size="small"
            variant="outlined"
            label={ORDER_SOURCE_LABELS[orderSource]}
            data-testid={`${dataTestId}-order-source`}
          />
        )}
        {reordering ? (
          <>
            <Button
              size="small"
              variant="contained"
              onClick={order.saveReorder}
              disabled={order.busy}
              data-testid={`${dataTestId}-reorder-save`}
            >
              Save order
            </Button>
            <Button
              size="small"
              onClick={order.cancelReorder}
              disabled={order.busy}
              data-testid={`${dataTestId}-reorder-cancel`}
            >
              Cancel
            </Button>
          </>
        ) : (
          order.canReorder && (
            <>
              <Tooltip title="Reorder values">
                <span>
                  <IconButton
                    onClick={order.startReorder}
                    size="small"
                    disabled={order.busy}
                    data-testid={`${dataTestId}-reorder-button`}
                  >
                    <ReorderIcon />
                  </IconButton>
                </span>
              </Tooltip>
              {hasOwnOrder && (
                <Tooltip title="Reset order">
                  <IconButton
                    onClick={() => setResetOpen(true)}
                    size="small"
                    data-testid={`${dataTestId}-reset-order-button`}
                  >
                    <ResetOrderIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )
        )}

        {/* Import Button - disabled for source fields */}
        {onOpenImport && (
          <Tooltip title={isSource ? 'Import not available for source fields' : 'Import Values'}>
            <span>
              <IconButton
                onClick={onOpenImport}
                size="small"
                disabled={isSource}
                data-testid={`${dataTestId}-import-button`}
              >
                <ImportIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* Export Button */}
        {onOpenExport && (
          <Tooltip title="Export Values">
            <IconButton
              onClick={onOpenExport}
              size="small"
              data-testid={`${dataTestId}-export-button`}
            >
              <ExportIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    [
      isSource,
      onOpenImport,
      onOpenExport,
      dataTestId,
      order,
      orderSource,
      inActiveTenant,
      reordering,
      hasOwnOrder,
    ]
  );

  // Show empty state if no field definition selected
  if (!selectedFieldDefinition) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }} data-testid={`${dataTestId}-empty-state`}>
        <SettingsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="textSecondary" gutterBottom>
          Select Field Definition and Tenant
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Choose a field definition and tenant context to manage master data values
        </Typography>
      </Paper>
    );
  }

  return (
    <Box data-testid={dataTestId}>
      {order.error && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid={`${dataTestId}-order-error`}>
          {order.error}
        </Alert>
      )}
      <ConfirmDialog
        open={resetOpen}
        title="Reset order?"
        confirmLabel="Reset order"
        onConfirm={async () => {
          setResetOpen(false);
          await order.resetOrder();
        }}
        onClose={() => setResetOpen(false)}
        dataTestId={`${dataTestId}-reset-order-dialog`}
      >
        {resetMessage}
      </ConfirmDialog>
      <MDTable
        mode="metadata"
        metadata={{
          // Use source field's ID for columns if this is a source field
          modelId: effectiveFieldDefForColumns?.fielddef_id || selectedFieldDefinition.fielddef_id,
          viewType: 'table',
          tenantId: selectedTenant,
          title: selectedFieldDefinition.label || 'Master Data Values',
          columnsBeforeRelatedFields: columnsBeforeRelatedFields,
          columnsAfterRelatedFields: columnsAfterRelatedFields,
        }}
        data={(order.reorderRows ?? values).map((item) => ({ ...item, id: item.id }))}
        loading={loading}
        error={error}
        onRowClick={(row) => onRowClick(row)}
        onRefresh={onRefresh}
        onAddNew={!isSource ? onCreateValue : undefined}
        showRefreshButton={true}
        showAddButton={!isSource}
        selectable={false}
        reorderable={reordering}
        reorderLabelField="name"
        onRowReorder={reordering ? order.setReorderRows : undefined}
        defaultColumnVisibility={{
          chip_color: false,
          created_at: false,
          created_by: false,
        }}
        sortable={true}
        filterable={true}
        customToolbarFilters={customToolbarFilters}
        customHeaderActions={customHeaderActions}
        valueChipColorLookup={chipColorLookup}
      />
    </Box>
  );
};

export default MDMValueTable;
